import { useState } from 'react'
import {
    List,
    ListItem,
    IconButton,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Typography,
} from '@mui/material'
import {
    Grid,
    Autocomplete as UniversalAutoComplete,
} from '../../UniversalComponents'
import DeleteIcon from '@mui/icons-material/Delete'
import PersonIcon from '@mui/icons-material/Person'

interface Props {
    manufacturerName: string
    manufacturerAdmins: any[]
    manufacturerNotificationAdmins: any[]
    onNotificationAdminsChange: (newNotificationAdmins: any[]) => void
    permissionCheck?: boolean
}

const ManufacturerNotificationAdmins = ({
    manufacturerName,
    manufacturerAdmins,
    manufacturerNotificationAdmins,
    onNotificationAdminsChange,
    permissionCheck,
}: Props) => {
    const [findUser, setFindUser] = useState('')

    const handleUserChange = (e: any) => {
        const { value } = e.target

        if (!value || !value._id) {
            return
        }

        const user = {
            _id: value._id,
            userEmail: value?.userEmail,
            userFirstName: value?.userFirstName,
            userLastName: value?.userLastName,
        }

        const doesUserExist = manufacturerNotificationAdmins?.some(
            (u) => u._id === user._id
        )

        if (!doesUserExist) {
            onNotificationAdminsChange([
                ...(manufacturerNotificationAdmins || []),
                user,
            ])
            setFindUser('')
        }
    }

    const onUserDelete = (id: string) => {
        const remaining = (manufacturerNotificationAdmins || []).filter(
            (u) => u._id !== id
        )
        onNotificationAdminsChange(remaining)
    }

    return (
        <Grid container>
            <Grid item xs={12} md={12}>
                <Typography
                    sx={{ marginTop: 2, marginBottom: 2 }}
                    align="center"
                    variant="body1"
                    color="primary"
                >
                    Select administrators from{' '}
                    <strong>{manufacturerName}</strong> to receive manufacturer
                    notifications
                </Typography>
            </Grid>

            <Grid item xs={12} md={12}>
                <UniversalAutoComplete
                    filterOptions={(options: any[], state: any) => {
                        if (!options) return []

                        const inputValue = (state?.inputValue || '')
                            .toLowerCase()
                            .trim()

                        return options.filter((option: any) => {
                            if (
                                manufacturerNotificationAdmins?.some(
                                    (selected: any) =>
                                        selected?._id === option?._id
                                )
                            ) {
                                return false
                            }

                            if (!inputValue) return true

                            const email = (option.userEmail || '').toLowerCase()
                            const firstName = (
                                option.userFirstName || ''
                            ).toLowerCase()
                            const lastName = (
                                option.userLastName || ''
                            ).toLowerCase()
                            const fullName = `${firstName} ${lastName}`.trim()

                            return (
                                email.includes(inputValue) ||
                                firstName.includes(inputValue) ||
                                lastName.includes(inputValue) ||
                                fullName.includes(inputValue)
                            )
                        })
                    }}
                    getOptionLabel={(option) => {
                        if (!option || typeof option !== 'object') {
                            return ''
                        }

                        const firstName = option.userFirstName || ''
                        const lastName = option.userLastName || ''
                        const email = option.userEmail || ''

                        const fullName = [firstName, lastName]
                            .filter(Boolean)
                            .join(' ')

                        return fullName ? `${fullName} (${email})` : email
                    }}
                    options={manufacturerAdmins || []}
                    isOptionEqualToValue={(option, value) =>
                        option._id === value._id
                    }
                    handleChange={handleUserChange}
                    value={findUser}
                    name="manufacturerNotificationAdmins"
                    fullWidth
                    placeholder="Enter an email or name to search..."
                    noOptionsText={'No remaining administrators found'}
                    label={'Manufacturer Administrators'}
                    disabled={permissionCheck}
                />
            </Grid>

            <Grid item xs={12} md={12}>
                <List sx={{ mt: 2 }}>
                    {(manufacturerNotificationAdmins || []).map((user) => (
                        <ListItem
                            key={user._id}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    title="Remove Notification Administrator"
                                    onClick={() => onUserDelete(user._id)}
                                    disabled={permissionCheck}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            }
                        >
                            <ListItemAvatar>
                                <Avatar>
                                    <PersonIcon />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={user.userEmail}
                                secondary={`${user?.userFirstName || ''} ${
                                    user?.userLastName || ''
                                }`.trim()}
                            />
                        </ListItem>
                    ))}
                </List>
            </Grid>
        </Grid>
    )
}

export default ManufacturerNotificationAdmins
